import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { api, fileUrl } from "@/lib/api";
import { ProductCard, SkeletonCard } from "@/components/ProductCard";
import { ArrowRight, MapPin, Mail, Phone } from "lucide-react";
import { HeroSlider, CraftVideos, ReelsSection } from "@/components/HomeMedia";
import { HomeIntro, TypewriterYear, Testimonials } from "@/components/HomeSections";
import { useCms } from "@/lib/cms";
import { menuCategories } from "@/components/Layout";
import { useSiteSettings, MAP_EMBED_URL, telHref } from "@/lib/site";

export default function Home() {
  const site = useSiteSettings();
  const intro = useCms("home_intro");
  const sections = useCms("home_sections");
  const journey = useCms("home_journey");
  const testimonials = useCms("home_testimonials");
  const { data: products } = useQuery({ queryKey: ["featured"], queryFn: () => api.get("/products?featured=true&limit=8").then(r => r.data) });
  const { data: home, isLoading: homeLoading } = useQuery({
    queryKey: ["home-content"],
    queryFn: () => api.get("/home/content").then(r => r.data),
    staleTime: 60 * 1000,
  });
  const { data: cats } = useQuery({ queryKey: ["cats"], queryFn: () => api.get("/categories").then(r => r.data), staleTime: 60 * 1000 });
  const { data: allProducts } = useQuery({ queryKey: ["shop-all"], queryFn: () => api.get("/products?limit=500&sort=newest").then(r => r.data), staleTime: 60 * 1000 });
  const shopCats = React.useMemo(() => {
    const list = cats || [];
    return menuCategories(list).map((c) => {
      const ids = new Set([c.id, ...list.filter((x) => x.parent_id === c.id).map((x) => x.id)]);
      const prods = (allProducts || []).filter((p) => ids.has(p.category_id));
      const withImg = prods.find((p) => (p.images || []).length);
      return { ...c, count: prods.length, cover: c.image_url || (withImg ? withImg.images[0] : null) };
    });
  }, [cats, allProducts]);


  return (
    <div>
      {/* HERO — sliding banners managed by Super Admin (Admin → Home Page) */}
      <HeroSlider banners={home?.banners} loading={homeLoading}/>

      {/* NEW — "The Avishka Navya Promise": story, animated numbers and promises (Admin → Content) */}
      <HomeIntro data={intro}/>

      {/* SHOP BY CATEGORIES — the categories ticked "Show in Shop menu" (Admin → Categories); click opens that category */}
      {shopCats.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-20" data-testid="shop-by-categories">
          <div className="text-center mb-10 sm:mb-12">
            {sections.categories_overline && <div className="overline mb-3">{sections.categories_overline}</div>}
            <h2 className="h2-editorial">{sections.categories_title}</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 lg:gap-8">
            {shopCats.map((c, i) => (
              <motion.div key={c.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: (i % 3) * 0.08 }} viewport={{ once: true }}>
                <Link to={`/shop/${c.slug}`} className="block group image-hover-zoom relative aspect-[4/5] rounded-2xl overflow-hidden shadow-sm bg-neutral-200 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_28px_50px_-14px_rgba(14,77,52,0.55)]" data-testid={`category-card-${c.slug}`}>
                  {c.cover
                    ? <>
                        {/* full photo is always visible (no cropping); a blurred copy fills any empty space */}
                        <img src={fileUrl(c.cover)} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-70" loading="lazy"/>
                        <img src={fileUrl(c.cover)} alt={c.name} className="relative w-full h-full object-contain" loading="lazy"/>
                      </>
                    : <div className="w-full h-full bg-gradient-to-br from-[#0E4D34] to-[#2D7A5B] flex items-center justify-center text-white/30 text-6xl font-semibold">{c.name.slice(0, 1)}</div>}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent"/>
                  <div className="absolute bottom-3 left-3 right-3 sm:bottom-6 sm:left-6 sm:right-6 text-white">
                    <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.3em] mb-1 sm:mb-2 opacity-80">{c.count} piece{c.count === 1 ? "" : "s"}</div>
                    <h3 className="text-lg sm:text-2xl lg:text-3xl font-medium leading-tight mb-1 sm:mb-2">{c.name}</h3>
                    <div className="hidden sm:flex items-center text-xs uppercase tracking-widest opacity-90 group-hover:opacity-100">Shop now <ArrowRight size={12} className="ml-2 group-hover:translate-x-1 transition-transform"/></div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-8 sm:mt-10">
            <Link to="/shop" className="btn-outline-silk rounded-[10px]">View all products <ArrowRight size={13} className="ml-2"/></Link>
          </div>
        </section>
      )}

      {/* PRODUCTS IN LIMELIGHT (featured products — tick "Featured" on a product in Admin → Products) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 bg-[#FAF9F6]" data-testid="limelight">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            {sections.limelight_overline && <div className="overline mb-2">{sections.limelight_overline}</div>}
            <h2 className="h2-editorial">{sections.limelight_title}</h2>
          </div>
          <Link to="/shop" className="btn-outline-silk" data-testid="view-all-products">{sections.limelight_button || "View All"}</Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-8">
          {products ? products.slice(0, 8).map((p, i) => <ProductCard key={p.id} product={p} index={i}/>) : Array.from({length: 8}).map((_, i) => <SkeletonCard key={i}/>)}
        </div>
      </section>

      {/* REELS — YouTube frames managed in Admin → Home Page → Reels; clicking a reel opens its own link */}
      <ReelsSection reels={home?.reels} section={home?.reels_section} instagramUrl={home?.instagram_url || site.instagram}/>

      {/* JOURNEY TIMELINE — years type themselves out as you scroll (Admin → Content → Home: Our Journey) */}
      {journey.enabled !== false && (journey.items || []).length > 0 && (
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-24" data-testid="journey">
        <div className="text-center mb-14">
          {journey.overline && <div className="overline mb-3">{journey.overline}</div>}
          <h2 className="h2-editorial mb-4">{journey.title}</h2>
          {journey.subtitle && <p className="text-neutral-600 max-w-2xl mx-auto">{journey.subtitle}</p>}
        </div>
        <div className="relative">
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-[#0E4D34]/20 hidden md:block"/>
          {journey.items.map((item, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className={`grid md:grid-cols-2 gap-8 mb-14 items-center ${i%2 ? "md:[&>*:first-child]:order-2" : ""}`}>
              <div className={`${i%2 ? "md:text-left md:pl-12" : "md:text-right md:pr-12"}`}>
                <div className="text-4xl font-serif text-[#0E4D34] mb-2 tabular-nums" data-testid={`journey-year-${i}`}><TypewriterYear text={item.year}/></div>
                <h3 className="font-serif text-2xl mb-2">{item.title}</h3>
                <p className="text-neutral-600">{item.body}</p>
              </div>
              <div className="hidden md:block relative h-1 bg-[#0E4D34]/20">
                <div className={`absolute top-1/2 -translate-y-1/2 ${i%2 ? "-left-2" : "-right-2"} w-4 h-4 rounded-full bg-[#991B1B] border-4 border-white`}/>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
      )}

      {/* CRAFTSMANSHIP VIDEO(S) — managed by Super Admin, YouTube, plays continuously */}
      <CraftVideos videos={home?.craft_videos}/>

      {/* TESTIMONIALS — Admin → Content → Home: Testimonials */}
      <Testimonials data={testimonials}/>

      {/* MAP + CONTACT */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20">
        <div className="grid md:grid-cols-2 gap-10">
          <div className="aspect-[4/3] rounded-2xl overflow-hidden">
            <iframe title="Avishka Navya Collections — store location" src={MAP_EMBED_URL}
                    className="w-full h-full border-0 rounded-2xl" allowFullScreen loading="lazy"
                    referrerPolicy="strict-origin-when-cross-origin"/>
          </div>
          <div>
            <div className="overline mb-3">Visit our Boutique</div>
            <h2 className="h2-editorial mb-6">Book a Personal Styling Session</h2>
            <div className="space-y-4 mb-8 text-neutral-700">
              {site.address && <div className="flex gap-3"><MapPin className="text-[#0E4D34] shrink-0 mt-1" size={18}/><div className="whitespace-pre-line">{site.address}</div></div>}
              {site.phone && <div className="flex gap-3"><Phone className="text-[#0E4D34] shrink-0 mt-1" size={18}/><a href={telHref(site.phone)} className="hover:underline">{site.phone}</a></div>}
              <div className="flex gap-3"><Mail className="text-[#0E4D34] shrink-0 mt-1" size={18}/><a href={`mailto:${site.email}`} className="hover:underline">{site.email}</a></div>
            </div>
            <Link to="/contact" className="btn-silk">Get in Touch</Link>
          </div>
        </div>
      </section>

    </div>
  );
}
